import React, { useEffect, useState } from 'react';
import './VirtualMallTeam.scss';
import { virtualMallUserTreeApi } from './VirtualMallApi/api';
import { RotatingLines } from 'react-loader-spinner';
import { toast } from 'react-toastify';

const TreeNode = ({ member, onView, level = 0 }) => {

    const hasChildren = member.children && member.children.length > 0;
    return (
        <div className="tree-node">
            <div className="node-content">
                <div className={`member-card ${member.status?.toLowerCase() || 'active'} ${member.level === 'Level 0' ? 'root-member' : ''}`}>
                    <div className="member-avatar">
                        {member.name.charAt(0)}
                    </div>
                    <div className="member-info">
                        <h4>{member.name}</h4>
                        <span className="member-level">{member.level}</span>
                        <span className={`status-dot ${member.status?.toLowerCase() || 'active'}`}></span>
                    </div>
                    <div className="member-actions">
                        <button
                            className="action-btn-small"
                            title="View Tree"
                            onClick={() => onView(member)}
                        >
                            👁️
                        </button>
                        <button className="action-btn-small" title="Message">💬</button>
                    </div>

                    <div className="member-tooltip">
                        <div className="tooltip-item">
                            <span className="label">Name:</span>
                            <span className="value">{member.name}</span>
                        </div>
                        <div className="tooltip-item">
                            <span className="label">ID:</span>
                            <span className="value">{member.referralId}</span>
                        </div>
                        {member.designation && (
                            <div className="tooltip-item designation-tooltip-item">
                                <span className="value">{member.designation}</span>
                            </div>
                        )}
                        {member.mobile && (
                            <div className="tooltip-item">
                                <span className="label">Mobile:</span>
                                <span className="value">{member.mobile}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {hasChildren && (
                <div className="node-children">
                    {member.children.map((child) => (
                        <TreeNode key={child.id} member={child} onView={onView} level={level + 1} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default function VirtualMallTeam() {

    // Transform API data to component state structure
    const mapApiDataToTree = (apiNode, defaultLevel = 0) => {
        if (!apiNode) return null;

        const level = apiNode.level !== undefined ? apiNode.level : defaultLevel;

        return {
            id: apiNode.vu_id,
            name: apiNode.vu_name || apiNode.vu_referal_id || 'Member',
            referralId: apiNode.vu_referal_id || 'N/A',
            mobile: apiNode.vu_mobile || '',
            designation: apiNode.designation || 'Member',
            level: `Level ${level}`,
            status: apiNode.status ? (apiNode.status.charAt(0).toUpperCase() + apiNode.status.slice(1)) : 'Active',
            children: (apiNode.children || []).map(child => mapApiDataToTree(child, level + 1))
        };
    };

    const [currentRoot, setCurrentRoot] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [isGeneratedSingleId, setIsGeneratedSingleId] = useState(false);

    const fetchUserTree = async (userId = null) => {
        const data = {
            levels: "2",
            ...(userId && { userid: userId })
        }
        setLoading(true);
        setLoadError("");
        try {
            const response = await virtualMallUserTreeApi(data);
            console.log(response.data, 'treeApi----');
            if (response && response.status && response.data) {
                if (!userId) {
                    setIsGeneratedSingleId(Boolean(response.data.is_single_id_generated));
                }
                const transformedData = mapApiDataToTree(response.data);
                if (transformedData) {
                    return transformedData;
                }
            }
            setLoadError("Failed to load team hierarchy");
        } catch (error) {
            console.error("Error fetching user tree:", error);
            setLoadError("Failed to load team hierarchy");
            toast.error("Failed to load team hierarchy");
        } finally {
            setLoading(false);
        }
        return null;
    };

    useEffect(() => {
        const loadInitialTree = async () => {
            const data = await fetchUserTree();
            if (data) {
                setCurrentRoot(data);
            }
        };
        loadInitialTree();
    }, []);

    const handleViewMember = async (member) => {
        // Optimistically add current root to history
        const previousRoot = currentRoot;
        const newTreeData = await fetchUserTree(member.id);

        if (newTreeData) {
            setHistory(prev => [...prev, previousRoot]);
            setCurrentRoot(newTreeData);
        }
    };

    const handleBack = () => {
        if (history.length === 0) return;
        const prev = history[history.length - 1];
        const newHistory = history.slice(0, -1);
        setCurrentRoot(prev);
        setHistory(newHistory);
    };

    const handleReset = () => {
        if (history.length > 0) {
            setCurrentRoot(history[0]);
            setHistory([]);
        }
    };

    if (loading && !currentRoot) {
        return (
            <div className="dashboard-section team-tree-section" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <RotatingLines strokeColor="grey" strokeWidth="5" animationDuration="0.75" width="48" visible={true} />
            </div>
        );
    }

    if (!currentRoot) {
        return (
            <div className="dashboard-section team-tree-section">
                <h2>Team Hierarchy</h2>
                <p>{loadError || "No team members found."}</p>
            </div>
        );
    }

    if (isGeneratedSingleId && history.length === 0) {
        return (
            <div className="dashboard-section team-tree-section">
                <h2>Team Hierarchy</h2>
                <p>Single ID generated accounts are not shown in the Team section.</p>
            </div>
        );
    }

    return (
        <div className="dashboard-section team-tree-section">
            <div className="section-header">
                <h2>Team Hierarchy - {currentRoot.name}</h2>
                <div className="header-actions">
                    {history.length > 0 && (
                        <>
                            <button className="secondary-btn" onClick={handleBack} style={{ marginRight: '10px' }}>
                                ⬅ Back
                            </button>
                            <button className="secondary-btn" onClick={handleReset} style={{ marginRight: '10px' }}>
                                🏠 Main Root
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="tree-container">
                {loading && (
                    <div className="tree-loading-overlay">
                        <RotatingLines strokeColor="grey" strokeWidth="5" animationDuration="0.75" width="30" visible={true} />
                    </div>
                )}
                <div className={`tree-wrapper ${loading ? 'loading-blur' : ''}`}>
                    <TreeNode member={currentRoot} onView={handleViewMember} />
                </div>
            </div>
            <style>{`
                .tree-loading-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(255, 255, 255, 0.5);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 10;
                }
                .tree-container {
                    position: relative;
                }
                .loading-blur {
                    opacity: 0.5;
                    pointer-events: none;
                }
            `}</style>
        </div>
    );
}
